import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {EntityPerformanceChartComponent, PerformanceRecord} from "./entity-performance-chart.component";
import {Connection, ConnectionType, DataType, MeterTreeMeasurementsResult, MeterType} from "@flowmaps-com/flowmaps-typescriptmodels";
import {lodash, none} from "../../../common/utils";
import {map, mergeMap, Observable, of, Subscription} from "rxjs";
import {RefdataUtils} from "../../refdata/refdata-utils";
import {Entity, EntityType} from "../../../handlers/entity";
import {sendQuery} from "../../../common/app-common-utils";
import {Measurements} from "../../measurements-component/measurements";
import {DataQueryFilters} from "../../measurements-component/measurements-handler.component";
import {Handler} from "../../../common/handler";
import {ChartDataPerMeasurement} from "../../../utils/chart-data-provider";
import {Chart} from "chart.js";
import {ActiveElement, ChartEvent} from "chart.js/dist/types";
import {
    buildMeterDisplayHierarchy,
    effectiveMeterType,
    isChargePointMeter
} from '../../refdata/meters/submeter-role.utils';
import {
    defaultIntermediateConsumptionType,
    intermediateConsumptionTypeForPeriod,
    intermediateConsumptionTypesWithData,
    meterTreeName
} from '../intermediate-meter.utils';
import {AppContext} from '../../../app-context';
import {MeterTreeNamePreferenceService} from '../meter-tree-name-preference.service';

@Component({
    selector: 'app-meter-performance-chart',
    // The Handler subclass wrapper otherwise loses these inputs in JIT compilation.
    inputs: ['selectedLocation', 'meterTreeMeasurements'],
    templateUrl: './entity-performance-chart.component.html',
    styleUrls: ['./entity-performance-chart.component.scss']
})
@Handler()
export class MeterPerformanceChartComponent extends EntityPerformanceChartComponent implements OnInit, OnChanges, OnDestroy {
    private dashboardMeasurements: ChartDataPerMeasurement;
    private meterNameSubscription: Subscription;
    selectedDataType: DataType = DataType.electricityIntermediateConsumption;
    perSquareMeterEnabled = true;
    override compactTitle = true;
    @Input() selectedLocation: Entity | null = null;

    @Input()
    set meterTreeMeasurements(result: MeterTreeMeasurementsResult | null) {
        this.dashboardMeasurements = result ? {byEntity: result.byMeter, totals: result.byConnection} : null;
        if (this.options) this.applyMeasurements();
    }

    private applyMeasurements() {
        if (this.dashboardMeasurements) this.selectIntermediateConsumptionTypeForPeriod(this.dashboardMeasurements);
        this.refresh();
    }

    constructor(private readonly meterTreeNamePreference: MeterTreeNamePreferenceService) {
        super();
    }

    ngOnInit() {
        super.ngOnInit();
        this.options.meterTreeView.selectedMeterName =
            this.meterTreeNamePreference.selectedName(AppContext.userProfile?.userId);
        this.meterNameSubscription = this.meterTreeNamePreference.selectedName$.subscribe(selectedName => {
            if (!selectedName || !this.options?.meterTreeView) return;
            this.options.meterTreeView.selectedMeterName = selectedName;
            this.refresh();
        });
        this.selectDefaultIntermediateConsumptionType();
        this.options.selectedDataType = this.selectedDataType;
        this.sendQuery("getEntityConsumptionType").subscribe(e => {
            if (e && this.possibleDataTypes().includes(e)) {
                this.options.selectedDataType = this.selectedDataType = e;
                this.selectDefaultIntermediateConsumptionType();
            }
        });
        this.subscribeTo("getChartDataQueryFilters")
            .subscribe((f: DataQueryFilters) => {
                this.filters = f;
                this.startLoading();
            });
        if (this.selectedLocation) {
            this.applyMeasurements();
        } else {
            this.subscribeTo("getDashboardMeasurements").subscribe({
                next: (data: ChartDataPerMeasurement) => {
                    this.dashboardMeasurements = data;
                    this.applyMeasurements();
                },
                error: this.showLoadError
            });
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.selectedLocation) {
            this.selectDefaultIntermediateConsumptionType();
        }
    }

    ngOnDestroy() {
        this.meterNameSubscription?.unsubscribe();
    }

    get getSelectedDataType() {
        const selectedDataType = super.getSelectedDataType;
        return selectedDataType && this.possibleDataTypes().includes(selectedDataType) ? selectedDataType : this.selectedDataType;
    }

    entityType = (): EntityType => EntityType.meter

    refresh = () => {
        this.startLoading();
        if (this.dashboardMeasurements) this.setData(this.dashboardMeasurements);
    }

    possibleDataTypes = (): DataType[] => this.selectedLocation
        ? [DataType.electricityIntermediateConsumption, DataType.waterIntermediateConsumption]
        : [DataType.electricityIntermediateConsumption, DataType.waterIntermediateConsumption,
            DataType.chargePointIntermediateConsumption]

    createData = (result: ChartDataPerMeasurement): Observable<PerformanceRecord[]> => {
        return this.sendQuery("getChartDataQueryFilters").pipe(mergeMap((d: DataQueryFilters) =>
            this.getEntities().pipe(map(meters => {
                    const displayHierarchyByConnectionId = new Map<string, ReturnType<typeof buildMeterDisplayHierarchy>>();
                    const displayHierarchy = (connection: Connection) => {
                        if (!displayHierarchyByConnectionId.has(connection.connectionId)) {
                            displayHierarchyByConnectionId.set(connection.connectionId,
                                buildMeterDisplayHierarchy(connection.meters || []));
                        }
                        return displayHierarchyByConnectionId.get(connection.connectionId)!;
                    };
                    return this.filterMetersBasedOnSelectedDataType(meters)
                        .filter(entity => displayHierarchy(entity.connection).visibleIds.has(entity.meter?.meterId || ''));
                }),
                map(meters => {
                    const displayHierarchyByConnectionId = new Map<string, ReturnType<typeof buildMeterDisplayHierarchy>>();
                    const displayHierarchy = (connection: Connection) => {
                        if (!displayHierarchyByConnectionId.has(connection.connectionId)) {
                            displayHierarchyByConnectionId.set(connection.connectionId,
                                buildMeterDisplayHierarchy(connection.meters || []));
                        }
                        return displayHierarchyByConnectionId.get(connection.connectionId)!;
                    };
                    const filteredData = result?.byEntity?.map(
                        m => new Measurements(m, this.getSelectedDataType, d.timeRange)) || [];
                    return Object.entries(lodash.groupBy(filteredData, d => d.data.entityId))
                        .map(([entityId, dataset]) => ({
                            meter: meters.find(l => l.meter.meterId === entityId),
                            dataset,
                        }))
                        .filter(e => e.meter)
                        .map(e => {
                            const area = RefdataUtils.getMeterArea(e.meter.meter);
                            const value = lodash.sum(e.dataset.flatMap(d => d.groupMeasurements()));
                            return <PerformanceRecord>{
                                entityId: e.meter.meter.meterId,
                                value: this.showPerSquareMeter() ? (area ? lodash.round(value / area, 1) : null) : value,
                                estimatedValue: null,
                                label: meterTreeName(e.meter, this.options.meterTreeView.selectedMeterName),
                                parentId: displayHierarchy(e.meter.connection).parentIds.get(e.meter.meter.meterId),
                                childIds: displayHierarchy(e.meter.connection).childIds.get(e.meter.meter.meterId) || [],
                                virtual: e.meter.meter.info.virtual,
                                type: effectiveMeterType(e.meter.meter)}
                        });
                }))));
    }

    private getEntities = (): Observable<Entity[]> => {
        if (this.selectedLocation?.location) {
            const {organisation, location} = this.selectedLocation;
            return of(location.connections.flatMap(connection => (connection.meters || [])
                .map(meter => new Entity(organisation, location, connection, meter))));
        }
        return this.sendQuery("getChartDataQueryFilters").pipe(mergeMap((q: DataQueryFilters) => {
            if (!q) {
                return of([]);
            }
            const selectedEntities: Entity[] = q.sources?.flatMap(s => s.source.getMeters()) || [];
            return selectedEntities.length > 0 ? of(selectedEntities) : this.getAllEntities();
        }))
    }

    getAllEntities = (): Observable<Entity[]> => sendQuery("getMetersAsEntities");

    filterMetersBasedOnSelectedDataType = (selection: Entity[]) => {
        const entities = selection.filter(s => none(selection, (e) => e.getParentAsEntity().getEntityId() === s.getEntityId())
            || (s.connection.info.connectionType == ConnectionType.ChargePoint && s.meter.info.type == MeterType.PRIMARY));
        switch (this.getSelectedDataType) {
            case DataType.electricityConsumption:
            case DataType.electricityConsumptionOffPeak:
            case DataType.electricityFeedIn:
            case DataType.electricityFeedInOffPeak:
                return entities.filter(e => e.meter && effectiveMeterType(e.meter) === MeterType.PRIMARY && e.connection?.info.connectionType === "Electricity");
            case DataType.electricityIntermediateConsumption:
            case DataType.electricityIntermediateConsumptionReactive:
                return entities.filter(e => e.meter
                    && effectiveMeterType(e.meter) === MeterType.INTERMEDIATE
                    && e.connection?.info.connectionType === "Electricity"
                    && !isChargePointMeter(e));
            case DataType.electricityGrossProductionReactive:
            case DataType.electricityGrossProduction:
                return entities.filter(e => e.meter && (e.meter.info?.type == "GROSS_PRODUCTION" || e.meter.details?.type == "GROSS_PRODUCTION") && e.connection?.info.connectionType === "Electricity");
            case DataType.gasConsumption:
                return entities.filter(e => e.meter && e.connection?.info.connectionType === "Gas");
            case DataType.heatConsumption:
                return entities.filter(e => e.meter && e.connection?.info.connectionType === "Heat");
            case DataType.waterIntermediateConsumption:
                return entities.filter(e => e.meter && effectiveMeterType(e.meter) === MeterType.INTERMEDIATE && e.connection?.info.connectionType === "Water");
            case DataType.waterConsumption:
                return entities.filter(e => e.meter && effectiveMeterType(e.meter) !== MeterType.INTERMEDIATE && e.connection?.info.connectionType === "Water");
            case DataType.chargePointConsumption:
                return entities.filter(e => e.meter && effectiveMeterType(e.meter) !== MeterType.INTERMEDIATE && e.connection?.info.connectionType === "ChargePoint");
            case DataType.chargePointIntermediateConsumption:
                return entities.filter(e => e.meter && effectiveMeterType(e.meter) === MeterType.INTERMEDIATE && e.connection?.info.connectionType === "ChargePoint");
            default:
                return entities;
        }
    }

    private selectIntermediateConsumptionTypeForPeriod(data: ChartDataPerMeasurement): void {
        if (!this.selectedLocation) return;
        this.selectedDataType = intermediateConsumptionTypeForPeriod(
            intermediateConsumptionTypesWithData(this.selectedLocation.location, data.byEntity), this.selectedDataType);
        this.options.selectedDataType = this.selectedDataType;
    }

    private selectDefaultIntermediateConsumptionType(): void {
        const defaultType = defaultIntermediateConsumptionType(this.selectedLocation?.location);
        if (this.selectedDataType !== defaultType) {
            this.selectedDataType = defaultType;
            if (this.options) this.options.selectedDataType = this.selectedDataType;
        }
    }

    navigateToEntity = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        const points = chart.getElementsAtEventForMode(event.native, 'nearest', {intersect: true}, true);

        if (points.length > 0) {
            const clickedRecord: PerformanceRecord = this.currentPageRecords[points[0].index];
            if (clickedRecord.entityId && clickedRecord.childIds?.length) {
                if (clickedRecord.type === MeterType.INTERMEDIATE) {
                    this.options.groupByEntityId = true;
                }
                this.sendCommandAndForget("selectEntity", clickedRecord.entityId);
            }
        } else {
            if (this.filters.sources?.length === 1) {
                let parent = this.filters.sources[0].source.getParentAsEntity();
                if (parent?.getEntityType() === EntityType.connection) {
                    parent = parent.getParentAsEntity();
                }
                if (parent) {
                    if (parent.getEntityType() === EntityType.organisation && this.organisations.length === 1) {
                        this.sendCommandAndForget("clearSelection");
                    } else {
                        this.sendCommandAndForget("selectEntity", parent.getEntityId());
                    }
                }
            }
        }
    }

    onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        const target: any = event.native.target;
        const points = chart.getElementsAtEventForMode(event.native, 'nearest', {intersect: true}, true);
        if (points.length > 0) {
            const clickedRecord: PerformanceRecord = this.currentPageRecords[points[0].index];
            if (clickedRecord.entityId && clickedRecord.childIds?.length) {
                target.style.cursor = "pointer";
            } else {
                target.style.cursor = "auto"
            }
        } else if (this.filters.sources?.length === 1) {
            const parent = this.filters.sources[0].source.getParentAsEntity();
            if (parent) {
                target.style.cursor = "zoom-out";
            } else {
                target.style.cursor = "auto"
            }
        } else {
            target.style.cursor = "auto";
        }
    }
}
